import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fonctions (onglets) activées pour tout le site, pilotées depuis le panel
  // (bouton « Fonctions visibles ») — distinct des permissions par utilisateur.
  const [modules, setModules] = useState({});
  const [centre, setCentre] = useState({ nom: "Centre de formation" });
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try {
        const { data: mv } = await api.get("/admin/modules-visibility");
        setModules(mv.enabled || {});
      } catch {}
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403 || userRef.current === null) setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCentre = useCallback(async () => {
    try {
      const { data } = await api.get("/public/centre");
      setCentre(data);
      document.title = data.nom || "Formation";
    } catch {}
  }, []);

  useEffect(() => { fetchMe(); fetchCentre(); }, [fetchMe, fetchCentre]);

  // Droits relus quand l'onglet redevient actif, et toutes les 5 minutes —
  // même filet de sécurité que sur le site de caisse (et c'est aussi ce qui
  // déconnecte un élève dont l'accès temporaire vient d'expirer).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchMe(); };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(fetchMe, 5 * 60 * 1000);
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(interval); };
  }, [fetchMe]);

  const login = async (matricule, pin) => {
    const { data } = await api.post("/auth/login", { matricule, pin });
    setUser(data.user);
    fetchMe();
    return data.user;
  };

  const loginBadge = async (badge_code) => {
    const { data } = await api.post("/auth/login-badge", { badge_code });
    setUser(data.user);
    fetchMe();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, modules, centre, login, loginBadge, logout, refresh: fetchMe, refreshCentre: fetchCentre }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function isEleve(user) {
  return !!user && !user.is_admin && user.user_type === "eleve";
}

export function hasPerm(user, module, action) {
  if (!user) return false;
  if (user.is_admin) return true;
  if (isEleve(user)) return false;
  const mod = (user.permissions || {})[module];
  if (typeof mod === "boolean") return mod;
  return !!(mod && mod[action]);
}

export function moduleVisible(modules, moduleId) {
  if (!modules || !(moduleId in modules)) return true;
  return !!modules[moduleId];
}
